import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { ActivatedRouteSnapshot, CanActivateChild, RouterStateSnapshot, UrlTree } from '@angular/router';
import jwtDecode from 'jwt-decode';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SessionExpirationGuard implements CanActivateChild {

  auth: Auth = inject(Auth);
  private readonly LAST_ACTIVITY_KEY = 'lastActivityTime';
  private lastActivityTimestamp: number = Date.now(); // In-memory timestamp
  private sessionCheckInterval: any;

  constructor(private authService: AuthService) {
    // Initialize from localStorage
    const stored = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    if (stored) {
      this.lastActivityTimestamp = new Date(stored).getTime();
    }
    // Update activity time on user interactions
    this.setupActivityTracking();
    // Start periodic session check
    this.startSessionMonitoring();
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    const lastSignInTime = this.auth.currentUser?.metadata.lastSignInTime;

    if (lastSignInTime == undefined) {
      return true;
    }

    // Restart monitoring if it was stopped (e.g., after logout/login)
    if (!this.sessionCheckInterval) {
      this.startSessionMonitoring();
    }

    // Use in-memory timestamp (not localStorage) to avoid race conditions
    const lastActivityTime = this.lastActivityTimestamp;
    const currentTime = Date.now();

    // Get session duration from token
    const token = localStorage.getItem('token');
    let sessionDurationHours = 1; // Default to 1 hour

    if (token) {
      try {
        const decodedToken: any = jwtDecode(token);
        sessionDurationHours = decodedToken.claims?.sessionDuration || 1;
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }

    const diffInMs = currentTime - lastActivityTime;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const hasSessionExpired = diffInHours >= sessionDurationHours;

    if (hasSessionExpired) {
      void this.authService.logout();
      return false;
    }

    // Update activity on successful navigation
    this.updateLastActivityTime();

    return true;
  }

  hasSessionExpired(lastActivityDate: Date, currentDate: Date, durationInHours: number): boolean {
    const diffInMs = Math.abs(currentDate.getTime() - lastActivityDate.getTime());
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return diffInHours >= durationInHours;
  }

  private updateLastActivityTime(): void {
    const now = Date.now();
    this.lastActivityTimestamp = now;
    localStorage.setItem(this.LAST_ACTIVITY_KEY, new Date(now).toISOString());
  }

  private setupActivityTracking(): void {
    const events = ['keydown', 'scroll', 'mousemove', 'touchmove'];
    events.forEach(event => {
      document.addEventListener(event, () => this.updateLastActivityTime(), { passive: true });
    });
  }

  private startSessionMonitoring(): void {
    // Check session every 10 seconds
    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionExpiration();
    }, 10000); // 10 seconds
  }

  private checkSessionExpiration(): void {
    // Don't check if user is not logged in
    if (!this.auth.currentUser) {
      return;
    }

    const currentTime = Date.now();
    const diffInMs = currentTime - this.lastActivityTimestamp;

    // Get session duration from token
    const token = localStorage.getItem('token');
    let sessionDurationHours = 1; // Default to 1 hour

    if (token) {
      try {
        const decodedToken: any = jwtDecode(token);
        sessionDurationHours = decodedToken.claims?.sessionDuration || 1;
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }

    const diffInHours = diffInMs / (1000 * 60 * 60);
    const hasSessionExpired = diffInHours >= sessionDurationHours;

    if (hasSessionExpired) {
      this.clearSessionMonitoring();
      void this.logoutExpiredSession();
    }
  }

  private async logoutExpiredSession(): Promise<void> {
    const loggedOut = await this.authService.logout();
    if (!loggedOut && this.auth.currentUser && !this.sessionCheckInterval) {
      this.startSessionMonitoring();
    }
  }

  private clearSessionMonitoring(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }
}
